/**
 * The audit log's filter state, and the one thing it must never do in the browser.
 *
 * ── Every narrowing is a server parameter ───────────────────────────────────
 *
 * `openapi.yaml` declares all eight — `log_type`, `actor_id`, `action`,
 * `resource_type`, `resource_id`, `from`, `to`, `success` — plus `cursor` and
 * `limit`. Not one of them is applied to rows after they arrive.
 *
 * That rule is stricter here than anywhere else on this build, and the reason is the
 * pagination. `GET /audit-logs` is **cursor**-paginated over two append-only tables
 * that grow while the page is open, and `CursorListEnvelope` publishes `next_cursor`
 * and `has_more` and **no total**. A browser-side filter would therefore produce a
 * count with nothing to check it against: an investigator who narrows to one actor
 * and sees three rows has to be able to report "three", and a client-side filter
 * would silently mean "three among the hundred that happened to come back".
 *
 * ── A changed filter is a new walk, not a page of the old one ───────────────
 *
 * `cursor` is opaque and positional. Carrying it across a filter change would ask the
 * server to continue a walk over a different query, which is meaningless at best.
 * `withFilters` therefore drops the cursor whenever any other field moves, and it is
 * a function rather than a convention in a component precisely so that it can be
 * driven directly and mutated.
 *
 * ── Empty means "do not narrow", and that is not the same as "match empty" ──
 *
 * A blank input sends **no parameter**. `qs` drops `undefined`, so an untouched field
 * is an absent parameter rather than `action=`, which the server would parse as a
 * filter on the empty verb and answer with nothing — an empty table an operator would
 * read as "nothing happened".
 */

import type { AuditLogTypeName } from '../api/types';
import { auditLogsPath } from '../api/paths';

/**
 * How many rows one read asks for.
 *
 * `openapi.yaml` allows 1 to 500 and defaults to 100. Explicit here for the reason
 * `paths.ts` gives about `granularity`: a screen whose meaning depends on a default
 * it does not state changes meaning when the default does — and this screen prints
 * the number it read, so the number has to be one it chose.
 */
export const AUDIT_PAGE_LIMIT = 100;

/** `success` is a tri-state on screen: both, only successes, only failures. */
export type SuccessFilter = 'any' | 'true' | 'false';

export interface AuditFilters {
  readonly logType: AuditLogTypeName | 'any';
  readonly actorId: string;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string;
  readonly from: string;
  readonly to: string;
  readonly success: SuccessFilter;
  /** The previous page's `page.next_cursor`, opaque and forwarded unread. */
  readonly cursor: string | null;
}

export const EMPTY_FILTERS: AuditFilters = {
  logType: 'any',
  actorId: '',
  action: '',
  resourceType: '',
  resourceId: '',
  from: '',
  to: '',
  success: 'any',
  cursor: null,
};

/** A field the operator left blank sends no parameter at all. */
function orUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * The request path for these filters.
 *
 * `logType: 'any'` and `success: 'any'` send **nothing**, which is `openapi.yaml`'s
 * own "omit for both tables". They are spelled `'any'` rather than `''`/`null` in the
 * state so that the radio group has a value to be checked against — a tri-state
 * rendered as two checkboxes is a control with an unreachable fourth combination.
 */
export function auditPath(filters: AuditFilters, limit: number = AUDIT_PAGE_LIMIT): string {
  return auditLogsPath({
    limit,
    logType: filters.logType === 'any' ? undefined : filters.logType,
    actorId: orUndefined(filters.actorId),
    action: orUndefined(filters.action),
    resourceType: orUndefined(filters.resourceType),
    resourceId: orUndefined(filters.resourceId),
    from: orUndefined(filters.from),
    to: orUndefined(filters.to),
    success: filters.success === 'any' ? undefined : filters.success === 'true',
    cursor: filters.cursor ?? undefined,
  });
}

/**
 * Apply a change to the filters, **dropping the cursor** unless the change is the
 * cursor itself.
 *
 * A cursor is a position in one query's ordering. Keeping it while the query changes
 * asks the server to continue a walk that no longer exists, and the answer would be
 * rows nobody can account for — in an audit log, of all places.
 */
export function withFilters(
  current: AuditFilters,
  change: Partial<Omit<AuditFilters, 'cursor'>>,
): AuditFilters {
  return { ...current, ...change, cursor: null };
}

/** Move to the next page, keeping every narrowing exactly as it stands. */
export function withCursor(current: AuditFilters, cursor: string): AuditFilters {
  return { ...current, cursor };
}

/** Is anything narrowed? Decides whether "empty" names the filter or the log. */
export function isNarrowed(filters: AuditFilters): boolean {
  return (
    filters.logType !== 'any' ||
    filters.success !== 'any' ||
    orUndefined(filters.actorId) !== undefined ||
    orUndefined(filters.action) !== undefined ||
    orUndefined(filters.resourceType) !== undefined ||
    orUndefined(filters.resourceId) !== undefined ||
    orUndefined(filters.from) !== undefined ||
    orUndefined(filters.to) !== undefined
  );
}
